// FASE 05 — Cadastros Gerais, Catálogos (plano de 23/09/2026, Lote D).
// No MOF antigo é a tela de Esquadrias 3D: código da tipologia, referência, linha, status e os dois
// modelos 3D (GLB e USDZ). Regra do legado mantida (P6): Disponível exige os dois modelos.
import { useEffect, useMemo, useState } from "react";
import { Autocomplete, Grid, MenuItem, Paper, TextField } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { debounce } from "lodash";
import NavigationHeader from "components/NavigationHeader";
import { StripedDataGrid } from "components/StripedDataGrid";
import { FloatMenuCrud } from "components/FloatMenuCrud";
import { FilterColumn } from "components/StripedDataGrid/FilterColumn";
import { TitleTable } from "components/TitleTable";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import { DashboardStackCustom, StackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import { TIME_DEBOUNCE } from "hooks/usePagination";
import { excluirCadastro, listarCadastro, listarTodos } from "services/modules/cadastrosGerais";
import { ModalCatalogo } from "./ModalCatalogo";

interface ICatalogoListagem {
  idEsquadria3D: number;
  codigo: string;
  referencia?: string;
  linha?: string;
  status?: string;
}

export const Catalogos = () => {
  const { openNotification } = useNavigation();
  const [linhas, setLinhas] = useState<ICatalogoListagem[]>([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [paginacao, setPaginacao] = useState({ page: 1, pageSize: 10, search: "" });
  // Filtros por referência e status (o backend também aceita linha; fica para quando houver pedido).
  const [filtroReferencia, setFiltroReferencia] = useState<{ id: number; label: string } | null>(null);
  const [filtroStatus, setFiltroStatus] = useState<number | "">("");
  const [referencias, setReferencias] = useState<{ id: number; label: string }[]>([]);
  const [modal, setModal] = useState<{ aberto: boolean; id?: number }>({ aberto: false });
  const [exclusao, setExclusao] = useState<{ aberto: boolean; id?: number }>({ aberto: false });
  const [excluindo, setExcluindo] = useState(false);

  const carregar = async () => {
    setCarregando(true);
    try {
      const pagina = await listarCadastro<ICatalogoListagem>("/Esquadria3D", {
        ...paginacao,
        ...(filtroReferencia ? { idReferencia: filtroReferencia.id } : {}),
        ...(filtroStatus !== "" ? { idStatus: filtroStatus } : {}),
      });
      setLinhas(pagina.itens);
      setTotal(pagina.total);
    } catch (error: any) {
      openNotification({ message: error?.message || "Não foi possível carregar os catálogos", type: "error" });
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginacao, filtroReferencia, filtroStatus]);

  useEffect(() => {
    listarTodos<any>("/Referencia")
      .then((lista) => setReferencias(lista.map((r) => ({ id: r.idReferencia, label: r.descricao }))))
      .catch(() => setReferencias([]));
  }, []);

  const filtrar = useMemo(
    () => debounce((texto: string) => setPaginacao((atual) => ({ ...atual, page: 1, search: texto })), TIME_DEBOUNCE),
    []
  );

  const excluir = async () => {
    if (!exclusao.id) return;
    setExcluindo(true);
    try {
      await excluirCadastro("/Esquadria3D", "idEsquadria3D", exclusao.id);
      openNotification({ message: "Catálogo excluído com sucesso!", type: "success" });
      carregar();
    } catch (error: any) {
      openNotification({ message: error?.message || "Não foi possível excluir o catálogo", type: "error" });
    } finally {
      setExcluindo(false);
      setExclusao({ aberto: false });
    }
  };

  const colunas: GridColDef[] = [
    {
      field: "codigo",
      headerName: "Código da Tipologia",
      sortable: false,
      flex: 0.8,
      renderHeader: () => (
        <FilterColumn label="Código da Tipologia" name="codigo" onChange={(evento) => filtrar(evento.target.value)} />
      ),
    },
    { field: "referencia", headerName: "Referência", sortable: false, flex: 0.6 },
    { field: "linha", headerName: "Linha", sortable: false, flex: 0.6 },
    { field: "status", headerName: "Status", sortable: false, flex: 0.4 },
    {
      field: "acoes",
      headerName: "Ações",
      sortable: false,
      align: "center",
      headerAlign: "center",
      flex: 0.2,
      renderCell: (params) => (
        <FloatMenuCrud
          onEdit={() => setModal({ aberto: true, id: params.row.idEsquadria3D })}
          showDuplicateOption={false}
          deleteButtonText="Excluir"
          onDelete={() => setExclusao({ aberto: true, id: params.row.idEsquadria3D })}
        />
      ),
    },
  ];

  return (
    <DashboardStackCustom>
      <NavigationHeader
        title="CADASTRO DE CATÁLOGOS"
        label="Catálogos"
        buttonLabel="Cadastrar"
        buttonOnClick={() => setModal({ aberto: true })}
        hasBackButton={false}
      />
      <Grid container display="flex" alignItems="center">
        <StackCustom>
          <Grid container mb={4} spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TitleTable title="Listagem de catálogos do MOF" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                options={referencias}
                value={filtroReferencia}
                onChange={(_, opcao) => {
                  setFiltroReferencia(opcao);
                  setPaginacao((atual) => ({ ...atual, page: 1 }));
                }}
                getOptionLabel={(opcao) => opcao.label}
                isOptionEqualToValue={(opcao, valor) => opcao.id === valor.id}
                renderInput={(params) => <TextField {...params} label="Referência" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={filtroStatus}
                onChange={(evento) => {
                  setFiltroStatus(evento.target.value === "" ? "" : Number(evento.target.value));
                  setPaginacao((atual) => ({ ...atual, page: 1 }));
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value={1}>Disponível</MenuItem>
                <MenuItem value={0}>Indisponível</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          <Paper>
            <StripedDataGrid
              rows={linhas}
              columns={colunas}
              getRowId={(linha: ICatalogoListagem) => linha.idEsquadria3D}
              page={paginacao.page - 1}
              pageSize={paginacao.pageSize}
              rowCount={total}
              loading={carregando || excluindo}
              onPageChange={(pagina) => setPaginacao((atual) => ({ ...atual, page: pagina + 1 }))}
              onPageSizeChange={(tamanho) => setPaginacao((atual) => ({ ...atual, pageSize: tamanho, page: 1 }))}
              checkboxSelection={false}
              rowHeight={70}
            />
          </Paper>
        </StackCustom>
      </Grid>

      <ModalCatalogo
        aberto={modal.aberto}
        idEsquadria3D={modal.id}
        onFechar={(houveAlteracao) => {
          setModal({ aberto: false });
          if (houveAlteracao) carregar();
        }}
      />

      <ConfirmDeleteModal
        title="Tem certeza que deseja excluir o catálogo?"
        description="Os modelos 3D enviados pelo MOF novo também serão apagados. Essa operação não pode ser desfeita."
        open={exclusao.aberto}
        onClose={() => setExclusao({ aberto: false })}
        onConfirm={excluir}
        isLoadingDelete={excluindo}
      />
    </DashboardStackCustom>
  );
};
